import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { atom_dropPinMode } from '@app/shared/state/drop-pin-mode.atom';
import { atom_isDesktop } from '@app/shared/state/tour.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_showModalContent, atom_modalType } from '@app/shared/state/show-content-modal.atom';
import { TourPinV2 } from '@app/shared/components/tour-pin/TourPinV2';
import { PlaceSearchInline } from '@app/shared/components/add-a-place/PlaceSearchInline';
import { PlaceDetails } from '@app/shared/state/user-added-stops.atom';

interface MapDropPinControlsProps {
  mapRef: React.MutableRefObject<any>;
  getPinSize: (zoom: number) => number;
  currentZoom: number;
  tourState: any;
  userAddedPlaces: Record<string, PlaceDetails[]>;
  setUserAddedPlaces: (val: any) => void;
}

export const MapDropPinControls = React.memo(function MapDropPinControls({
  mapRef,
  getPinSize,
  currentZoom,
  tourState,
  userAddedPlaces,
  setUserAddedPlaces,
}: MapDropPinControlsProps) {
  const { t } = useTranslation();
  const [dropPinMode, setDropPinMode] = useRecoilState(atom_dropPinMode);
  const isDesktop = useRecoilValue(atom_isDesktop);
  const setListMode = useSetRecoilState(atom_listModeState);
  const setState_sideBar = useSetRecoilState(sideBarStateAtom);
  const setState_showModal = useSetRecoilState(atom_showModalContent);
  const setState_modalType = useSetRecoilState(atom_modalType);

  // Hide list when positioning popup is shown (mobile only)
  useEffect(() => {
    if (dropPinMode.step === 'positioning' && !isDesktop) {
      setListMode(null);
    }
  }, [dropPinMode.step, isDesktop, setListMode]);

  const [dropPinSearchFocused, setDropPinSearchFocused] = useState(false);
  const dropPinContainerRef = useRef<HTMLDivElement>(null);

  const handleSearchFocusChange = useCallback((focused: boolean) => {
    setDropPinSearchFocused(focused);
    if (!isDesktop && mapRef.current) {
      const size = getPinSize(currentZoom);
      const offset = size * 1.8 + size;
      mapRef.current.panBy([0, focused ? -offset : offset], { animate: true, duration: 0.3 });
      if (dropPinContainerRef.current) {
        dropPinContainerRef.current.style.transition = 'transform 0.3s ease';
        dropPinContainerRef.current.style.transform = focused ? `translateY(${offset}px)` : 'translateY(0)';
      }
    }
  }, [isDesktop, getPinSize, currentZoom, mapRef]);

  const dropPinSearchNameRef = useRef<string | null>(null);

  const handleSearchSelected = useCallback((details: PlaceDetails) => {
    dropPinSearchNameRef.current = details.name || null;
    if (mapRef.current) {
      mapRef.current.flyTo(
        [details.geometry.location.lat, details.geometry.location.lng],
        17,
        { duration: 1.5 }
      );
    }
  }, [mapRef]);

  if (dropPinMode.step !== 'positioning') return null;

  const size = getPinSize(currentZoom);

  return (
    <Box
      ref={dropPinContainerRef}
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 1001,
        width: 0,
        height: 0,
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: size * 1.8,
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          p: 2,
          width: isDesktop ? '450px' : '95vw',
          maxWidth: '450px',
        }}
      >
        {!dropPinSearchFocused && (
          <Typography sx={{ fontSize: 14, color: '#666', mb: 1.5, textAlign: 'center' }}>
            {t('drop_pin_note') || 'Move the map to position the pin at your desired location.'}
          </Typography>
        )}
        <Box sx={{ mb: 1.5 }}>
          <PlaceSearchInline onPlaceSelected={handleSearchSelected} onFocusChange={handleSearchFocusChange} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              setDropPinMode({ isActive: false, step: null, pinLocation: null });
              if (!isDesktop) {
                const defaultMode = tourState?.config?.['show_list'] ? 'mobileList' : 'mobileSwiper';
                setListMode(defaultMode as any);
                setState_sideBar(null);
              }
            }}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: '#ddd',
              color: '#666',
            }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              if (mapRef.current) {
                const center = mapRef.current.getCenter();
                const tourId = tourState?.id || '';
                const searchName = dropPinSearchNameRef.current || null;
                const existingPlaceId = dropPinMode.tempPlaceId;
                let placeId = existingPlaceId;

                if (tourId) {
                  const currentTourPlaces = userAddedPlaces[tourId] || [];
                  if (existingPlaceId) {
                    setUserAddedPlaces({
                      ...userAddedPlaces,
                      [tourId]: currentTourPlaces.map((p: PlaceDetails) =>
                        p.place_id === existingPlaceId
                          ? { ...p, geometry: { location: { lat: center.lat, lng: center.lng } } }
                          : p
                      ),
                    });
                  } else {
                    placeId = `temp_${Date.now()}`;
                    const tempPlace: PlaceDetails = {
                      place_id: placeId,
                      name: searchName || '',
                      description: '',
                      geometry: { location: { lat: center.lat, lng: center.lng } },
                      icon: 'user',
                      tags: [],
                      placeType: 'dropped_pin',
                    };
                    setUserAddedPlaces({
                      ...userAddedPlaces,
                      [tourId]: [...currentTourPlaces, tempPlace],
                    });
                  }
                }

                if (dropPinContainerRef.current) {
                  dropPinContainerRef.current.style.transition = 'transform 0.3s ease';
                  dropPinContainerRef.current.style.transform = 'translateY(0)';
                }
                setDropPinMode({
                  isActive: true,
                  step: 'naming',
                  pinLocation: { lat: center.lat, lng: center.lng },
                  searchName,
                  tempPlaceId: placeId,
                });
                dropPinSearchNameRef.current = null;
                if (isDesktop) {
                  setState_modalType('dropAPin');
                  setState_showModal(true);
                } else {
                  setListMode('mobileAddPlace');
                }
              }
            }}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              bgcolor: '#48526B',
              '&:hover': { bgcolor: '#3a4259' },
            }}
          >
            {t('continue') || 'Continue'}
          </Button>
        </Box>
      </Box>
      <div style={{ pointerEvents: 'none' }}>
        <TourPinV2
          visited={false}
          active={false}
          disabled={false}
          pinStrokeColor="#48526B"
          size={size}
          isDesktop={isDesktop}
          id="drop-pin-center"
        >
          <i className="fa-solid fa-user" style={{ fontSize: '14px' }} aria-hidden="true" />
        </TourPinV2>
      </div>
    </Box>
  );
});
